import { AxiosResponse } from 'axios';
import { throttle } from '../config';
import { PaginationAndFilter } from '../interfaces/common/pagination';
import type { BrandModel, BrandFormModel } from '../interfaces/masters/base/brand';
import { applyFilters } from '../utils/apply-filters';
import { applyPagination } from '../utils/apply-pagination';
import { applySort } from '../utils/apply-sort';
import { wait } from '../utils/wait';
import axiosClient from '../utils/axios-client';

interface Options {
  filters?: Array<{ operator: string; property: string; value: any }>;
  sort?: string;
  sortBy?: string;
  page?: number;
  query?: string;
  view?: string;
}

class BrandApi {
  // calls GET API to get all the brands
  async getBrands(options: Options):
  Promise<{ brands: BrandModel[], brandsCount: number }> {
    if (throttle) {
      await wait(throttle);
    }

    const brands : BrandModel[] = await axiosClient.get('master/brand',).then((response) => (
      response.data
    ));

    const { filters, sort, sortBy, page, query, view } = options;

    /*
     NOTE: Query, filter, sort and pagination are operation meant to be executed on the server.
     Since this does not connect to a real backend, we simulate these operations.
     */

    const queriedBrands = brands.filter((_brand) => {
      // If query exists, it looks only in customer full name field
      if (!!query && !(
        _brand.name?.toLowerCase().includes(query.toLowerCase())
        || _brand.id?.toLowerCase().includes(query.toLowerCase()))) {
        return false;
      }

      // No need to look for any resource fields
      if (typeof view === 'undefined' || view === 'all') {
        return true;
      }

      return true;
    });
    const filteredBrands = applyFilters(queriedBrands, filters);
    const sortedBrands = applySort(filteredBrands, sort, sortBy);
    const paginatedBrands = applyPagination(sortedBrands, page);

    return Promise.resolve({
      brands: paginatedBrands,
      brandsCount: filteredBrands.length
    });
  }

  async getAllBrands():Promise<BrandModel[]> {
    if (throttle) {
      await wait(throttle);
    }

    const brands : BrandModel[] = await axiosClient.get('master/brand',).then((response) => (
      response.data
    ));

    return Promise.resolve(brands);
  }

  // calls api to get the category by id
  async getBrandById(brandId: string): Promise<BrandModel> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get(`master/brand/getById/${brandId}`);

    return response.data;
  }

  // calls POST api to add brand in the database
  async addBrand(brand: BrandFormModel): Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.post('master/brand', brand);

    return response;
  }

  // calls PUT api to update brand
  async updateBrand(brand: BrandFormModel): Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.put('master/brand', brand);

    return response;
  }

  // calls DELETE api to delete brand in the database
  async deleteBrand(brandId: string): Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.delete(`master/brand/${brandId}`);

    return response;
  }

  // get brands based on options filter
  async getBrandByOptions(options: any):
  Promise<AxiosResponse> {
    if (throttle) {
      await wait(throttle);
    }

    const response = await axiosClient.get('master/brand/getByOptions', { params: options });

    return response;
  }

  async getBrandsByPaginationAndFilters(options: PaginationAndFilter):
  Promise<{ responseData: any, statusCode: number }> {
    if (throttle) {
      await wait(throttle);
    }
    try {
      const res = await axiosClient.post('master/brand/getByPaginationAndFilter', options).then((response) => response);

      return Promise.resolve({
        responseData: res.data,
        statusCode: res.status
      });
    } catch (error) {
      return Promise.resolve({
        responseData: error.response.data,
        statusCode: error.response.status
      });
    }
  }
}

export const brandApi = new BrandApi();
